import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from '@services/toast.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '@services/auth.service';
import { OTP_DIGITS, OTP_DURATION_SECONDS } from '@core/models/auth.model';

@Component({
  selector: 'app-change-password-verification-dialog',
  imports: [FormsModule],
  templateUrl: './change-password-verification-dialog.html',
  styleUrl: './change-password-verification-dialog.scss'
})
export class ChangePasswordVerificationDialog implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly spinner = inject(NgxSpinnerService);

  @Input() uniqueId: string = '';
  @Input() userId: string = '';
  @Input() emailId: string = '';
  @Input() oldPassword: string = '';
  @Input({ required: true }) newPassword!: string;
  @Input() confirmPassword: string = '';

  @Output() confirmed = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChildren('digitInput') private readonly digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  protected readonly digits = signal<string[]>(Array(OTP_DIGITS).fill(''));

  protected readonly otp = signal('');
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);
  protected readonly resending = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly infoMessage = signal<string | null>(null);
  protected readonly remainingSeconds = signal(OTP_DURATION_SECONDS);
  protected readonly resendEnabled = signal(false);

  private timerHandle?: ReturnType<typeof setInterval>;

  get minutes(): string {
    return Math.floor(this.remainingSeconds() / 60)
      .toString()
      .padStart(2, '0');
  }

  get seconds(): string {
    return (this.remainingSeconds() % 60).toString().padStart(2, '0');
  }

  ngOnInit(): void {
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  onOtpDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);

    const next = [...this.digits()];
    next[index] = value;
    this.digits.set(next);
    input.value = value;
    this.emitOtp();

    if (value && index < next.length - 1) {
      this.focusOtpIndex(index + 1);
    }
  }

  onOtpKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      if (!this.digits()[index] && index > 0) {
        this.focusOtpIndex(index - 1);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusOtpIndex(index - 1);
    } else if (event.key === 'ArrowRight' && index < OTP_DIGITS - 1) {
      this.focusOtpIndex(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') ?? '';
    const cleanNumbers = pastedData.replace(/\D/g, '').slice(0, OTP_DIGITS);

    if (!cleanNumbers) {
      return;
    }

    const next = Array(OTP_DIGITS).fill('');
    const inputElements = this.digitInputs?.toArray() ?? [];

    for (let i = 0; i < cleanNumbers.length; i++) {
      next[i] = cleanNumbers[i];
      if (inputElements[i]) {
        inputElements[i].nativeElement.value = cleanNumbers[i];
      }
    }

    this.digits.set(next);
    this.emitOtp();

    const nextFocusIndex = Math.min(cleanNumbers.length, OTP_DIGITS - 1);
    this.focusOtpIndex(nextFocusIndex);
  }

  private clearOtp(): void {
    const next = Array(OTP_DIGITS).fill('');
    const inputElements = this.digitInputs?.toArray() ?? [];
    inputElements.forEach((el) => (el.nativeElement.value = ''));
    this.digits.set(next);
    this.emitOtp();
    this.focusOtpIndex(0);
  }

  private focusOtpIndex(index: number): void {
    setTimeout(() => {
      this.digitInputs?.toArray()[index]?.nativeElement.focus();
    });
  }

  private emitOtp(): void {
    this.otp.set(this.digits().join(''));
  }

  verify(): void {
    this.submitted.set(true);
    this.errorMessage.set(null);

    if (this.otp().length !== 6) {
      this.toast.error('Authentication code is required.');
      this.errorMessage.set('Authentication code is required.');
      return;
    }

    this.loading.set(true);
    this.spinner.show();
    const payload = {
      OTP: this.otp(),
      UserId: this.userId,
      UniqueId: this.uniqueId,
      NewPassword: this.newPassword
    };

    this.authService.verifyChangePasswordOtp(payload).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.spinner.hide();
        if (response.success) {
          const msg = response.message || 'Password updated successfully.';
          this.toast.success(msg);
          this.infoMessage.set(msg);
          this.confirmed.emit();
        } else {
          const msg = response.message || 'Invalid or expired authentication code.';
          this.toast.error(msg);
          this.errorMessage.set(msg);
          this.otp.set('');
          this.clearOtp();
        }
      },
      error: (error) => {
        this.loading.set(false);
        this.spinner.hide();
        this.otp.set('');
        this.clearOtp();
        const msg = error?.error?.message ?? 'Invalid or expired authentication code.';
        this.toast.error(msg);
        this.errorMessage.set(msg);
      }
    });
  }

  resend(): void {
    if (!this.resendEnabled()) {
      return;
    }

    this.resending.set(true);
    this.spinner.show();
    this.errorMessage.set(null);
    this.infoMessage.set(null);

    const payload = {
      OldPassword: this.oldPassword,
      CreatePassword: this.newPassword,
      ConfirmPassword: this.confirmPassword
    };

    this.authService.requestChangePassword(payload).subscribe({
      next: (response) => {
        this.resending.set(false);
        this.spinner.hide();
        if (response.success) {
          const uid = response.data?.UniqueId;
          if (uid) {
            this.uniqueId = uid;
          }
          const msg = response.message || 'A new authentication code has been sent.';
          this.toast.success(msg);
          this.infoMessage.set(msg);
          this.otp.set('');
          this.clearOtp();
          this.resendEnabled.set(false);
          this.remainingSeconds.set(OTP_DURATION_SECONDS);
          this.startTimer();
        } else {
          const msg = response.message || 'Unable to resend code.';
          this.toast.error(msg);
          this.errorMessage.set(msg);
        }
      },
      error: (error) => {
        this.resending.set(false);
        this.spinner.hide();
        const msg = error?.error?.message ?? 'Unable to resend the code. Please try again.';
        this.toast.error(msg);
        this.errorMessage.set(msg);
      }
    });
  }

  private startTimer(): void {
    this.clearTimer();
    this.timerHandle = setInterval(() => {
      const next = this.remainingSeconds() - 1;
      if (next <= 0) {
        this.remainingSeconds.set(0);
        this.resendEnabled.set(true);
        this.clearTimer();
      } else {
        this.remainingSeconds.set(next);
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = undefined;
    }
  }
}
